import { useRef, useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectHistState } from "state/histSlice";
import type { StandardObject } from "utils";
import type { HandleRespResult, GenericResponse } from "services/utils";
import type { NavConfigGroup } from "components/layouts/config-nav";

interface UseSummaryHookProps<R> {
  getSummary: () => HandleRespResult<GenericResponse<R>>;
  formatSummary: (data: R | null) => NavConfigGroup[];
}

export const useSummary = <T extends StandardObject>({
  getSummary,
  formatSummary,
}: UseSummaryHookProps<T>): NavConfigGroup[] => {
  const pendingTask = useRef<NodeJS.Timeout | null>(null);
  const [summary, setSummary] = useState<NavConfigGroup[]>(formatSummary(null));
  const { serviceHistory } = useSelector(selectHistState);
  const handleGetSummary = useCallback(() => {
    getSummary()
      .then(([ret, errMsg]) => {
        if (!errMsg) {
          if (ret !== null && ret.data) {
            const formattedSummary = formatSummary(ret.data);
            setSummary(formattedSummary);
            setTimeout(() => {
              pendingTask.current = null;
            }, 3000);
          }
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }, [getSummary, formatSummary]);
  useEffect(() => {
    pendingTask.current = setTimeout(() => {
      handleGetSummary();
    }, 0);
  }, []);
  useEffect(() => {
    if (pendingTask.current === null) {
      pendingTask.current = setTimeout(() => {
        handleGetSummary();
      }, 0);
    }
  }, [serviceHistory]);
  return summary;
};
